/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React, {useState, useMemo, useEffect} from 'react';
import dateFormat from 'dateformat';

import {updateNomineeDetails} from 'api';
import {KYC_FIREBASE_EVENTS} from 'constants/kycFirebaseEvents';
import {triggerToast, handleApiError} from 'helpers';
import {logKycFirebaseEvent} from 'utils/logFirebaseEvents';
import {isMinor} from 'utils/dateUtils';
import NomineeDetails from './NomineeDetails';
import {useSkipStep} from 'hooks';
import {updateToast} from 'helpers/triggerToast';
import {getSchema} from './NomineeDetailsSchema';

import useRestApi from 'hooks/useRestApi';
import {getNomineeFormSchema} from 'api';
import {LoaderContainer, Spinner} from 'screens/commons';

const NomineeDetailsContainer = ({kycData, updateStatus}) => {
  const [isLoading, setIsLoading] = useState(false);
  const {skipStep, isLoading: isSkipLoading} = useSkipStep();

  const handleSkip = (e) => {
    e && e?.preventDefault();

    triggerToast('Skipping nominee details...', 'loading', {
      toastId: 'skip-nominee-details',
    });

    skipStep().then(() => {
      updateToast('skip-nominee-details', 'Nominee opted out', 'success');
      if (updateStatus) {
        updateStatus();
      }
    });
  };

  const {
    doApiCall,
    isLoading: isSchemaLoading,
    data: schemaData,
  } = useRestApi({
    apiFunction: getNomineeFormSchema,
    options: {
      onError: (error) => {
        handleApiError(error);
      },
    },
  });

  useEffect(() => {
    doApiCall();
  }, []);

  // Process schema with address options
  const processedSchema = useMemo(() => {
    if (!schemaData?.data) return null;
    const {schema, addresses} = schemaData?.data;
    return getSchema(structuredClone(schema), addresses);
  }, [schemaData?.data]);

  const onSubmit = async (updatedNomineeDetails) => {
    const validatedNominees = [];

    if (updatedNomineeDetails.length <= 0) {
      triggerToast('Atleast one nominee is required.');
    } else {
      for (let i = 0; i < updatedNomineeDetails.length; i++) {
        const nominee = updatedNomineeDetails[i];
        const isNomineeMinor = isMinor(nominee.dob);

        setIsLoading(true);
        const isPanId = nominee?.nominee_id_type === 'PAN';
        const isAadhaarId = nominee?.nominee_id_type === 'AADHAAR';
        const isPassportId = nominee?.nominee_id_type === 'PASSPORT';
        const nomPayload = {
          ...nominee,
          name: nominee.name,
          phone_number: nominee.phone_number,
          email: nominee.email,
          relation: nominee.relation,
          dob: nominee.dob
            ? dateFormat(nominee.dob, 'isoDate').split('-').reverse().join('/')
            : undefined,
          guardian_name: isNomineeMinor ? nominee?.guardian_name : undefined,
          guardian_dob:
            isNomineeMinor && nominee?.guardian_dob
              ? dateFormat(nominee.guardian_dob, 'isoDate').split('-').reverse().join('/')
              : undefined,

          guardian_pan_number: isNomineeMinor
            ? nominee?.guardian_pan_number?.toUpperCase()
            : undefined,
          nominee_relation_with_guardian: isNomineeMinor
            ? nominee?.nominee_relation_with_guardian
            : undefined,
          guardian_id_type: isNomineeMinor ? nominee?.guardian_id_type : undefined,
          guardian_aadhaar_number: isNomineeMinor ? nominee?.guardian_aadhaar_number : undefined,
          pan_number: isPanId ? nominee?.pan_number?.toUpperCase() : '',
          aadhaar_number: isAadhaarId ? nominee.aadhaar_number : '',
          passport_number: isPassportId ? nominee.passport_number : '',
          address: {
            line1: nominee.line1,
            line2: nominee.line2,
            line3: nominee.line3,
            pincode: nominee.pincode,
            district_or_city: nominee.district_or_city,
            country: nominee.country,
            address: `${nominee.line1},${nominee.line2},${nominee.line3},${nominee.district_or_city},${nominee.country},${nominee.pincode}`,
          },
          include_nominee_in_soa: nominee.include_nominee_in_soa === 'true' ? true : false,
          nominee_id_type: nominee.nominee_id_type,
          nominee_is_nri: nominee.nominee_is_nri === 'true' ? true : false,
        };
        validatedNominees.push(nomPayload);
      }
    }

    try {
      const res = await updateNomineeDetails({nominees: validatedNominees});
      if (res.status === 200) {
        logKycFirebaseEvent(kycData.flow, KYC_FIREBASE_EVENTS.NOMINATION_COMPLETED, {
          nomineeStatus: 'added',
        });
        updateStatus();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSchemaLoading || !schemaData?.data?.schema) {
    return (
      <LoaderContainer>
        <Spinner color="blue" message="Fetching Nominee Details..." />
      </LoaderContainer>
    );
  }

  return (
    <NomineeDetails
      isDisabled={false}
      onSubmit={onSubmit}
      isLoading={isLoading}
      schema={processedSchema}
      onSkip={handleSkip}
      isSkipLoading={isSkipLoading}
    />
  );
};

export default NomineeDetailsContainer;
